<script lang="ts" setup>
import { storeToRefs } from 'pinia'

import SpecialistCardFull from '~/components/cards/SpecialistCardFull.vue'
import SpecialityCarousel from '~/components/carousels/SpecialityCarousel.vue'
import FilterLabel from '~/components/filters/FilterLabel.vue'
import FilterWrapper from '~/components/filters/FilterWrapper.vue'
import BasicSelect from '~/components/form-elements/BasicSelect.vue'
import ToggleButton from '~/components/form-elements/ToggleButton.vue'
import GoalSection from '~/components/sections/GoalSection.vue'
import InterviewSection from '~/components/sections/InterviewSection.vue'
import SpecialistHeaderSection from '~/components/sections/SpecialistHeaderSection.vue'
import SkeletonSpecialistCardFull from '~/components/skeleton/SkeletonSpecialistCardFull.vue'
import SpecialistEmpty from '~/components/specialist/SpecialistEmpty.vue'
import SpecialistSubject from '~/components/specialist/SpecialistSubject.vue'
import { useResponsiveScreenFacade } from '~/composables/responsive-screen-facade'
import AuthScope from '~/enums/auth-scope'
import PageEnum from '~/enums/page'
import { SpecialistSort, SpecialistSortUtils } from '~/enums/specialist-sort'
import { useSpecialistsStore } from '~/store/specialists'
import { useUserStore } from '~/store/user'
import type { SelectOption } from '~/types/form-elements'

definePageMeta({
  layout: false
})

const sortOptions = Object.values(SpecialistSort)
  .filter(value => value !== SpecialistSort.ApplicationStatus)
  .map(value => ({
    name: SpecialistSortUtils.getName(value),
    value
  }))

const taxonomyLinks = [{ title: 'Специалисты', to: PageEnum.Specialists }]

const isStateLoading = ref(false)
const screenWidthResolution = ref(window?.innerWidth)
const specialityCarouselStatus = ref(true)
const specialityIssuesStatus = ref(true)
const route = useRoute()
const router = useRouter()

// const serviceSelectRef = ref()

const specialistsStore = useSpecialistsStore()
specialistsStore.isDataLoading = true
const { getSpecialists, filter } = storeToRefs(specialistsStore)
const userStore = useUserStore()

function onResize (): void {
  screenWidthResolution.value = window.innerWidth
}

async function changePage (pageNumber: number): Promise<void> {
  window.scrollTo(0, 0)

  specialistsStore.page.number = pageNumber

  await specialistsStore.fetchSpecialists(AuthScope.Client)
}

function hideBoxs (): void {
  if (specialistsStore.filter.query.toString() !== '') {
    specialityCarouselStatus.value = specialistsStore.filter.specialityId !== null
    specialityIssuesStatus.value = specialistsStore.filter.issuesId !== null
  } else {
    specialityCarouselStatus.value = true
    specialityIssuesStatus.value = true
  }
}

async function loadData (): Promise<void> {
  specialistsStore.page.number = 1

  hideBoxs()

  await specialistsStore.fetchSpecialists(AuthScope.Client)
}

async function setSpecialityId (specialityId: string): Promise<void> {
  specialistsStore.filter.specialityId = specialistsStore.filter.specialityId !== specialityId ? specialityId : null

  await loadData()
}

async function sort (x: SelectOption): Promise<void> {
  specialistsStore.sort = x.value

  await loadData()
}

watch(
  () => filter.value,
  (x) => {
    const query = {
      isOfflineConsultation: x.isOfflineConsultation,
      isOnlineConsultation: x.isOnlineConsultation,
      issuesId: x.issuesId,
      helpingAdults: x.helpingAdults,
      helpingChildren: x.helpingChildren,
      query: x.query,
      specialityId: x.specialityId
    }

    router.replace({ query })

    isStateLoading.value = true
    setTimeout(
      (): void => {
        isStateLoading.value = false
      },
      0
    )
  },
  { deep: true }
)

onMounted(async (): Promise<void> => {
  window.addEventListener('resize', onResize)
  onResize()

  specialistsStore.updateFilterFromQuery(route.query)

  await loadData()
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
  specialistsStore.clearFilter()
})
</script>

<template>
  <NuxtLayout name="specialist">
    <section class="min-h-screen overflow-visible">
      <Taxonomy :links="taxonomyLinks" />

      <SpecialistHeaderSection @on-search="hideBoxs" />

      <template v-if="specialityCarouselStatus">
        <strong class="text-main-black mt-7 block text-[24px] font-medium md:mt-[64px] md:text-[22px]">
          Категории <span class="hidden sm:inline-block">специалистов</span>
        </strong>

        <SpecialityCarousel
          :active-id="specialistsStore.filter.specialityId"
          class="mt-3 md:mt-5"
          @set-active-id="setSpecialityId"
        />
      </template>

      <SpecialistIssues
        v-if="specialityIssuesStatus"
        class="mt-[60px]"
      />

      <div class="mb-3 mt-8 flex items-start md:mb-6 md:mt-[60px]">
        <SpecialistSubject itemprop="about" subject="Все специалисты" />

        <p class="-mt-1 ml-1 select-none text-[17px] text-[#d1d1d1] md:-mt-4 md:text-[22px]">
          {{ specialistsStore.totalSpecialists }}
        </p>
      </div>

      <FilterWrapper>
        <template #left>
          <FilterLabel text="Тип приёма" />

          <ToggleButton
            class="mr-2 md:mx-2"
            :is-active="specialistsStore.filter.isOfflineConsultation"
            :is-gray="false"
            title="Очно"
            @click="
              specialistsStore.filter.isOfflineConsultation = !specialistsStore.filter.isOfflineConsultation;
              loadData()
            "
          />

          <ToggleButton
            class="mr-2"
            :is-active="specialistsStore.filter.isOnlineConsultation"
            :is-gray="false"
            title="Онлайн"
            @click="
              specialistsStore.filter.isOnlineConsultation = !specialistsStore.filter.isOnlineConsultation;
              loadData()
            "
          />

          <ToggleButton
            class="mr-2"
            :is-active="specialistsStore.filter.helpingChildren"
            :is-gray="false"
            title="Работает c детьми"
            @click="
              specialistsStore.filter.helpingChildren = !specialistsStore.filter.helpingChildren;
              loadData()
            "
          />

          <ToggleButton
            class="mr-3"
            :is-active="specialistsStore.filter.helpingAdults"
            :is-gray="false"
            title="Работает со взрослыми"
            @click="
              specialistsStore.filter.helpingAdults = !specialistsStore.filter.helpingAdults;
              loadData()
            "
          />
        </template>
        <template #right>
          <FilterLabel text="Сортировать" />

          <BasicSelect
            class="md:ml-2 md:mr-7 [&_button]:!min-w-[200px]"
            :options="sortOptions"
            placeholder="По умолчанию"
            @selected="sort"
          />

          <FilterLabel text="Доступные даты приёма" />

          <BasicSelect
            class="ml-2"
            :options="[
              { name: 'Любой день', value: '' },
              { name: 'Сегодня', value: '' },
              { name: 'Завтра', value: '' },
              // { name: 'Среда, 16 авг', value: '' },
              // { name: 'Четверг, 17 авг', value: '' }
            ]"
            placeholder="Любой день"
          />
        </template>
      </FilterWrapper>

      <div class="-mx-3 flex flex-wrap items-stretch">
        <template v-if="specialistsStore.isDataLoading">
          <div
            v-for="n in 9"
            :key="n"
            class="basis-full border-8 border-transparent sm:basis-1/2 lg:basis-1/3 xl:basis-1/4"
          >
            <SkeletonSpecialistCardFull />
          </div>
        </template>
        <template v-else-if="getSpecialists.length > 0">
          <div
            v-for="x in getSpecialists"
            :key="x.id"
            class="basis-full border-8 border-transparent sm:basis-1/2 lg:basis-1/3 xl:basis-1/4"
            itemscope
            itemtype="https://schema.org/Person"
          >
            <SpecialistCardFull itemprop="itemListElement" :specialist-data="x" />
          </div>
        </template>
      </div>

      <SpecialistEmpty v-if="!getSpecialists.length && !specialistsStore.isDataLoading" />

      <Pagination
        class="my-10 md:my-[60px]"
        :current-page="specialistsStore.page.number"
        itemprop="pagination"
        :last-page="specialistsStore.totalPages"
        @change-page="changePage"
      />

      <GoalSection />

      <InterviewSection class="mt-10 md:mt-[60px]" />
    </section>
  </NuxtLayout>
</template>
